import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Descriptions,
  Divider,
  Typography,
  Badge,
  Row,
  Col,
  Alert,
  Spin,
  message,
} from 'antd'
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { reviewAPI, dashboardAPI, type PendingReviewItem, type RequestDetails, type AnalystReviewPayload } from '../services/api'
import LLMNarrativeCard, { LLMNarrative } from '../components/LLMNarrativeCard'
import ValidationCollapsePanel from '../components/ValidationCollapsePanel'

const { Title, Text } = Typography
const { TextArea } = Input
const { Option } = Select

interface ReviewModalData extends RequestDetails {
  // Add any additional fields needed for review
}

const Review: React.FC = () => {
  const [pendingReviews, setPendingReviews] = useState<PendingReviewItem[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [reviewModalData, setReviewModalData] = useState<ReviewModalData | null>(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()

  const loadPendingReviews = async () => {
    setLoading(true)
    try {
      const data = await reviewAPI.getPending(50, 0)
      setPendingReviews(data)
    } catch (error) {
      message.error('Error al cargar solicitudes pendientes')
      console.error('Error loading pending reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadRequestDetails = async (profilingId: string) => {
    setModalLoading(true)
    try {
      const response = await dashboardAPI.getRequestDetails(profilingId)
      // Merge request data with validations
      const requestWithValidations = {
        ...response.request,
        validations: response.validations
      }
      setReviewModalData(requestWithValidations)
    } catch (error) {
      message.error('Error al cargar detalles de la solicitud')
      console.error('Error loading request details:', error)
    } finally {
      setModalLoading(false)
    }
  }

  const handleReview = async (profilingId: string) => {
    setModalVisible(true)
    form.resetFields()
    await loadRequestDetails(profilingId)
  }

  const handleSubmitReview = async (values: any) => {
    if (!reviewModalData) return

    setSubmitting(true)
    try {
      const payload: AnalystReviewPayload = {
        analyst_id: 'current_user', // TODO: Get from auth context
        analyst_name: 'Analista Actual', // TODO: Get from auth context
        review_notes: values.review_notes,
        final_decision: values.final_decision,
      }

      await reviewAPI.submitReview(reviewModalData.id, payload)
      message.success('Revisión enviada exitosamente')
      setModalVisible(false)
      loadPendingReviews() // Refresh the list
    } catch (error) {
      message.error('Error al enviar la revisión')
      console.error('Error submitting review:', error)
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    loadPendingReviews()
  }, [])

  const getValidationStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'passed': return 'success'
      case 'failed': return 'error'
      case 'warning': return 'warning'
      default: return 'default'
    }
  }

  const getDecisionColor = (decision: string) => {
    switch (decision) {
      case 'APPROVED': return 'success'
      case 'REJECTED': return 'error'
      case 'PENDING_REVIEW': return 'warning'
      case 'PROCESSING': return 'processing'
      default: return 'default'
    }
  }

  const columns = [
    {
      title: 'ID Simulación',
      dataIndex: 'simulation_id',
      key: 'simulation_id',
      width: 120,
    },
    {
      title: 'Cliente',
      key: 'customer',
      width: 200,
      render: (record: PendingReviewItem) => (
        <div>
          <div><strong>{record.customer_name || 'N/A'}</strong></div>
          <div><Text type="secondary">{record.customer_document || 'N/A'}</Text></div>
        </div>
      ),
    },
    {
      title: 'Tipo Cliente',
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 120,
      render: (type: string) => (
        <Tag color={type === 'PENSIONADO' ? 'blue' : 'green'}>
          {type || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Fecha Creación',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('es-CO'),
    },
    {
      title: 'Validaciones Pendientes',
      dataIndex: 'pending_validations',
      key: 'pending_validations',
      width: 200,
      render: (validations: string[]) => (
        <div>
          {validations?.map((validation, index) => (
            <Tag key={index} color="orange" style={{ marginBottom: 4 }}>
              {validation}
            </Tag>
          )) || <Text type="secondary">N/A</Text>}
        </div>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (record: PendingReviewItem) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleReview(record.profiling_id)}
            size="small"
          >
            Revisar
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              Revisión Manual
            </Title>
            <Text type="secondary">
              Solicitudes pendientes de revisión por analistas
            </Text>
          </div>
          <Badge count={pendingReviews.length} showZero>
            <Button onClick={loadPendingReviews} loading={loading}>
              Actualizar
            </Button>
          </Badge>
        </div>

        <Table
          columns={columns}
          dataSource={pendingReviews}
          rowKey="profiling_id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} solicitudes`,
          }}
        />
      </Card>

      <Modal
        title="Revisión de Solicitud"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={1000}
        footer={null}
      >
        <Spin spinning={modalLoading}>
          {reviewModalData && (
            <div>
              <Alert
                message="Revisión Manual Requerida"
                description="Esta solicitud requiere revisión manual por un analista. Por favor, revise los detalles y tome una decisión."
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
              />

              {/* Información del Cliente */}
              <Card title="👤 Información del Cliente" style={{ marginBottom: 16 }}>
                <Row gutter={24}>
                  <Col span={8}>
                    <Descriptions bordered size="small">
                      <Descriptions.Item label="Nombre Completo" span={3}>
                        <strong>{reviewModalData.customer_name || 'N/A'}</strong>
                      </Descriptions.Item>
                      <Descriptions.Item label="Documento" span={3}>
                        {reviewModalData.customer_document || 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Tipo Cliente" span={3}>
                        <Tag color={reviewModalData.customer_type === 'PENSIONADO' ? 'blue' : 'green'}>
                          {reviewModalData.customer_type || 'N/A'}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col span={8}>
                    <Descriptions bordered size="small">
                      <Descriptions.Item label="Pagaduría" span={3}>
                        {reviewModalData.pagaduria || 'No especificada'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Fecha Solicitud" span={3}>
                        {new Date(reviewModalData.created_at).toLocaleString('es-CO')}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col span={8}>
                    <Descriptions bordered size="small">
                      <Descriptions.Item label="Score de Crédito" span={3}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Badge
                            count={reviewModalData.credit_score || 0}
                            style={{
                              backgroundColor: reviewModalData.credit_score > 600 ? '#52c41a' : 
                                              reviewModalData.credit_score > 400 ? '#faad14' : '#f5222d'
                            }}
                          />
                          <Text type="secondary">
                            {reviewModalData.credit_score > 600 ? 'Excelente' :
                             reviewModalData.credit_score > 400 ? 'Aceptable' : 'Bajo'}
                          </Text>
                        </div>
                      </Descriptions.Item>
                      <Descriptions.Item label="Confianza IA" span={3}>
                        {reviewModalData.ai_confidence ? 
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag color={reviewModalData.ai_confidence > 0.7 ? 'green' : 'orange'}>
                              {(reviewModalData.ai_confidence * 100).toFixed(1)}%
                            </Tag>
                            <Text type="secondary">
                              {reviewModalData.ai_confidence > 0.7 ? 'Alta' : 'Media'}
                            </Text>
                          </div> : 'N/A'
                        }
                      </Descriptions.Item>
                      <Descriptions.Item label="Recomendación IA" span={3}>
                        <Tag color={getDecisionColor(reviewModalData.ai_recommendation)}>
                          {reviewModalData.ai_recommendation || 'N/A'}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>
              </Card>

              {/* Validaciones Detalladas */}
              <Card title="🔍 Validaciones Realizadas" style={{ marginBottom: 16 }}>
                {reviewModalData.validations && (
                  <ValidationCollapsePanel validations={reviewModalData.validations} />
                )}
              </Card>

              {/* Análisis de Scores */}
              <Card title="📈 Análisis de Scores" style={{ marginBottom: 16 }}>
                <Row gutter={24}>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: 16 }}>
                      <div style={{ fontSize: 18, color: '#1890ff' }}>Score Truora</div>
                      <div style={{ fontSize: 32, fontWeight: 'bold', margin: '8px 0' }}>
                        {reviewModalData.truora_data?.global_score || 'N/A'}
                      </div>
                      <div style={{ color: '#666' }}>Validación de identidad</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: 16 }}>
                      <div style={{ fontSize: 18, color: '#52c41a' }}>Score de Crédito</div>
                      <div style={{ fontSize: 32, fontWeight: 'bold', margin: '8px 0' }}>
                        {reviewModalData.credit_score || 0}
                      </div>
                      <div style={{ color: '#666' }}>Centrales de riesgo</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: 16 }}>
                      <div style={{ fontSize: 18, color: '#fa8c16' }}>Nivel de Confianza</div>
                      <div style={{ fontSize: 32, fontWeight: 'bold', margin: '8px 0' }}>
                        {reviewModalData.final_confidence_score ? 
                          `${(reviewModalData.final_confidence_score * 100).toFixed(1)}%` : 'N/A'}
                      </div>
                      <div style={{ color: '#666' }}>Sistema integrado</div>
                    </div>
                  </Col>
                </Row>
              </Card>

              {/* Datos Experian si están disponibles */}
              {reviewModalData.experian_data?.available && (
                <Card title="📊 Información Experian" style={{ marginBottom: 16 }}>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Descriptions bordered size="small">
                        <Descriptions.Item label="Cuentas en Cartera" span={2}>
                          {reviewModalData.experian_data.cuentas_cartera?.length || 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Saldo Total" span={2}>
                          ${(reviewModalData.experian_data.saldo_total || 0).toLocaleString('es-CO')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Saldo en Mora" span={2}>
                          ${(reviewModalData.experian_data.saldo_mora || 0).toLocaleString('es-CO')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Créditos Vigentes" span={2}>
                          {reviewModalData.experian_data.creditos_vigentes || 0}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                    <Col span={12}>
                      {reviewModalData.experian_data.saldo_mora > 0 && (
                        <Alert
                          message="Atención: Cliente con saldos en mora"
                          description={`Saldo en mora: $${reviewModalData.experian_data.saldo_mora.toLocaleString('es-CO')}`}
                          type="warning"
                          showIcon
                        />
                      )}
                    </Col>
                  </Row>
                </Card>
              )}

              {/* Motivo de Revisión Manual */}
              <Card title="⚠️ Motivo de Revisión Manual" style={{ marginBottom: 16 }}>
                <Alert
                  message={reviewModalData.decision_reason}
                  description="Esta solicitud fue enviada a revisión manual por el motivo indicado arriba. Como analista, debe evaluar si es apropiado aprobar, rechazar o solicitar información adicional."
                  type="info"
                  showIcon
                />
              </Card>

              {/* Narrativo LLM */}
              {(() => {
                const narrativeDetails = (reviewModalData.validations as any)?.llm_narrative?.details
                if (!narrativeDetails || typeof narrativeDetails !== 'object') return null
                return (
                  <LLMNarrativeCard
                    narrative={narrativeDetails as LLMNarrative}
                    decision={reviewModalData.decision_status}
                  />
                )
              })()}

              <Divider />

              <Form
                form={form}
                onFinish={handleSubmitReview}
                layout="vertical"
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="final_decision"
                      label="Decisión Final"
                      rules={[{ required: true, message: 'Por favor seleccione una decisión' }]}
                    >
                      <Select placeholder="Seleccione una decisión" size="large">
                        <Option value="APPROVED">
                          <CheckOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                          Aprobar
                        </Option>
                        <Option value="REJECTED">
                          <CloseOutlined style={{ color: '#f5222d', marginRight: 8 }} />
                          Rechazar
                        </Option>
                        <Option value="PENDING_REVIEW">
                          <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                          Requiere más información
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <div style={{ paddingTop: 30 }}>
                      <Text type="secondary">
                        <strong>Recomendación IA:</strong>{' '}
                        <Tag color={getDecisionColor(reviewModalData.ai_recommendation)}>
                          {reviewModalData.ai_recommendation || 'N/A'}
                        </Tag>
                      </Text>
                    </div>
                  </Col>
                </Row>

                <Form.Item
                  name="review_notes"
                  label="Notas de Revisión"
                  rules={[{ required: true, message: 'Por favor agregue notas de revisión' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Agregue sus observaciones y justificación de la decisión..."
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={() => setModalVisible(false)}>
                      Cancelar
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                    >
                      Enviar Revisión
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  )
}

export default Review